import type { EditPreferencesFormValues } from '../../../domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { SearchField } from '@sunwebgroup/uds-ui/fields/search-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import {
  filterPreferencesBySearch,
  hasPreferencesSelectionChanged,
  isPreferencesSearchNeeded,
  isPreferencesSearchWithoutResults,
} from '../../../domain/editPreferencesRules'
import { useBookingDetailsStore } from '../../../infrastructure/useBookingDetailsStore'
import { getEditPreferencesFormValues } from '../../selectors'
import styles from './EditPreferencesForm.module.css'
import { PreferenceField } from './PreferenceField'

export const EDIT_PREFERENCES_FORM_ID = 'edit-preferences-form'

export function EditPreferencesForm() {
  const { formatMessage } = useIntl()
  const { editPreferencesSubmitted, editPreferencesUnchangedSubmitted } = useBookingDetailsActions()
  const loadStatus = useBookingDetailsStore(state => state.editPreferences.loadStatus)
  const submitStatus = useBookingDetailsStore(state => state.editPreferences.submitStatus)
  const wasSubmittedUnchanged = useBookingDetailsStore(state => state.editPreferences.wasSubmittedUnchanged)
  const isStale = useBookingDetailsStore(state => state.editPreferences.isStale)
  const preferences = useBookingDetailsStore(state => state.editPreferences.items)
  const values = useBookingDetailsStore(getEditPreferencesFormValues)
  const methods = useForm<EditPreferencesFormValues>({ values })
  const editedPreferences = useWatch({ control: methods.control, name: 'preferences' })
  const hasChanges = hasPreferencesSelectionChanged(values.preferences, editedPreferences)
  const [searchTerm, setSearchTerm] = React.useState('')

  if (loadStatus === 'error') {
    return (
      <Banner hasIcon color="danger" size="M" contentIsCentered>
        <Banner.TextsWrapper>
          <Banner.Text>{formatMessage({ id: 'common_error_title' })}</Banner.Text>
          <Banner.SupportingText>{formatMessage({ id: 'common_error_message' })}</Banner.SupportingText>
        </Banner.TextsWrapper>
      </Banner>
    )
  }

  const visiblePreferences = filterPreferencesBySearch(preferences, searchTerm)
  const isSearchWithoutResults = isPreferencesSearchWithoutResults(preferences.length, visiblePreferences.length)
  const emptyListMessageId = isSearchWithoutResults
    ? 'live-search_no_results'
    : 'booking_details_preferences_edit_empty_message'
  const isSubmitting = submitStatus === 'loading'

  function handleSubmit(formValues: EditPreferencesFormValues) {
    return hasChanges ? editPreferencesSubmitted(formValues) : editPreferencesUnchangedSubmitted()
  }

  function showPreferencesHiddenBySearch() {
    setSearchTerm('')
  }

  return (
    <FormProvider {...methods}>
      {wasSubmittedUnchanged && !hasChanges && (
        <Banner hasIcon color="informative" size="M" className={styles.banner}>
          <Banner.TextsWrapper>
            <Banner.Text>{formatMessage({ id: 'common_no_changes_title' })}</Banner.Text>
            <Banner.SupportingText>{formatMessage({ id: 'common_no_changes_message' })}</Banner.SupportingText>
          </Banner.TextsWrapper>
        </Banner>
      )}
      {submitStatus === 'success' && !hasChanges && (
        <Banner hasIcon contentIsCentered color={isStale ? 'warning' : 'success'} size="M" className={styles.banner}>
          <Banner.TextsWrapper>
            <Banner.Text>
              {formatMessage({ id: isStale ? 'booking_details_preferences_edit_stale_message' : 'booking_details_preferences_edit_success_message' })}
            </Banner.Text>
          </Banner.TextsWrapper>
        </Banner>
      )}
      {submitStatus === 'error' && (
        <Banner hasIcon contentIsCentered color="danger" size="S" className={styles.banner}>
          <Banner.TextsWrapper>
            <Banner.Text>{formatMessage({ id: 'booking_details_preferences_edit_error_message' })}</Banner.Text>
          </Banner.TextsWrapper>
        </Banner>
      )}
      <div className={styles.intro}>
        <Typography as="p" variant="body-m-regular-tight">
          {formatMessage({ id: 'booking_details_preferences_edit_intro' })}
        </Typography>
        <Typography as="p" variant="body-m-regular-tight">
          <Typography as="strong" variant="body-m-bold-tight">
            {formatMessage({ id: 'booking_details_preferences_edit_medical_title' })}
          </Typography>
          {' '}
          {formatMessage({ id: 'booking_details_preferences_edit_medical_text' })}
        </Typography>
      </div>
      {isPreferencesSearchNeeded(preferences.length) && (
        <SearchField
          className={cn(styles.search, isSubmitting && styles.disabledWhileSubmitting)}
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          ariaLabel={formatMessage({ id: 'live-search_search_button_text' })}
          placeholder={formatMessage({ id: 'live-search_search_button_text' })}
          clearButtonAriaLabel={formatMessage({ id: 'clear_search_button_aria_label' })}
        />
      )}
      <form
        id={EDIT_PREFERENCES_FORM_ID}
        className={styles.form}
        onSubmit={methods.handleSubmit(handleSubmit, showPreferencesHiddenBySearch)}
        noValidate
      >
        <div className={cn(styles.preferences, isSubmitting && styles.disabledWhileSubmitting)}>
          {visiblePreferences.length === 0 && (
            <Banner hasIcon color="informative" size="M" contentIsCentered>
              <Banner.TextsWrapper>
                <Banner.Text>{formatMessage({ id: emptyListMessageId })}</Banner.Text>
              </Banner.TextsWrapper>
            </Banner>
          )}
          {preferences.map((preference, index) => (
            <PreferenceField
              key={preference.id}
              index={index}
              label={preference.text}
              type={preference.type}
              isHidden={!visiblePreferences.includes(preference)}
            />
          ))}
        </div>
      </form>
    </FormProvider>
  )
}
